import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Pulse,
  ArrowUpRight,
  Brain,
  CaretDown,
  ListMagnifyingGlass,
  LockKey,
  List,
  Microphone,
  Paperclip,
  Plus,
  ShieldCheck,
  SignOut,
  Wallet as WalletIcon,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useRazorpay } from "react-razorpay";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EnterprisePanel } from "../components/EkamEnterprisePanels";
import { AskEkamWidget } from "../components/AskEkam";

const EXAMPLES = [
  "Review this research literature.",
  "Build a software application.",
  "Analyze this business problem.",
  "Assess financial risk.",
  "Design a course.",
  "Analyze this scientific question.",
  "Map this regulation to our controls.",
  "Review this code.",
  "Design a clinical research workflow.",
  "Find the scientific evidence for this hypothesis.",
  "Build a multi-agent workflow.",
  "Help me discover something new.",
];

const STAGE_ORDER = ["PLANNING", "INDUSTRY_DETECTION", "CAPABILITY_SELECTION", "AGENT_SELECTION", "EVIDENCE_COLLECTION", "REASONING", "CRITIQUE", "VERIFICATION", "SYNTHESIS", "HUMAN_REVIEW"];
const NAV_ITEMS = ["Chats", "Projects", "Agents", "Workflows", "Research", "Tools", "Artifacts", "Connectors", "Plugins", "Images", "Coding", "Governance", "Settings"];

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

const estimateCredits = (prompt, industries, advanced) => Math.max(1, Math.ceil(prompt.length / 1500)) * 2 + (advanced ? 2 : 0) + Math.min((industries || []).length, 3) * 2;

const Panel = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-teal-500/15 bg-[#0a2527]/70 p-4 backdrop-blur-xl" data-testid={testId}>
    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">{title}</h3>
    <div className="mt-3">{children}</div>
  </div>
);

const ProofCenter = ({ proof }) => {
  if (!proof) return null;
  const items = [
    ["Runs", proof.runs],
    ["Evidence objects", proof.evidence_objects],
    ["Human reviews", proof.human_reviews],
    ["Audit events", proof.audit_events],
    ["Verification pass", proof.verification_pass_rate === "NOT YET MEASURED" ? "Not yet measured" : `${proof.verification_pass_rate}%`],
  ];
  return (
    <div className="mt-8 rounded-3xl border border-cyan-400/15 bg-black/20 p-5" data-testid="proof-center">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">Live Proof Center</p>
        <p className="text-[10px] text-slate-600">Live database metrics</p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {items.map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-white/[0.03] p-4">
            <p className="font-display text-2xl font-light text-white" data-testid={`proof-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState = ({ example, onExample, guest = false, proof = null }) => (
  <div className="mx-auto flex min-h-[62vh] max-w-5xl flex-col items-center justify-center text-center" data-testid="ekam-empty-state">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 shadow-[0_0_60px_rgba(6,182,212,0.15)]">
        <Brain size={30} weight="duotone" />
      </div>
      <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white">
        How can Ekam help you?
      </h1>
      <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
        Describe the outcome. Ekam orchestrates intelligence, agents, tools, knowledge and evidence around your goal.
      </p>
      <button type="button" onClick={() => onExample(example)} data-testid="ekam-example-prompt" className="mt-8 rounded-full border border-teal-500/15 bg-white/[0.03] px-5 py-3 text-sm text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-200">
        {example}
      </button>
      {guest && (
        <div className="mt-12 grid gap-3 text-left sm:grid-cols-2 lg:grid-cols-3" data-testid="ekam-public-benefits">
          {[
            ["Discover", "Knowledge, evidence and opportunities"],
            ["Decide", "Risks, choices and priorities"],
            ["Build", "Agents, workflows and applications"],
            ["Create", "Documents, articles and campaigns"],
            ["Operate", "Governed work with audit trails"],
            ["Transform", "Functions, models and organizations"],
          ].map(([title, text]) => (
            <div key={title} className="rounded-2xl border border-teal-500/15 bg-white/[0.03] p-5 backdrop-blur-xl">
              <p className="text-sm font-bold text-cyan-200">{title}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{text}</p>
            </div>
          ))}
        </div>
      )}
      {guest && <ProofCenter proof={proof} />}
    </motion.div>
  </div>
);

const StageTimeline = ({ stages }) => (
  <div className="space-y-3" data-testid="ekam-execution-stages">
    {STAGE_ORDER.map((stage) => {
      const event = stages.find((item) => item.stage === stage);
      return (
        <div key={stage} className="flex items-start gap-3">
          <span className={`mt-1 h-2.5 w-2.5 rounded-full ${event ? "bg-cyan-300 shadow-[0_0_16px_rgba(6,182,212,0.7)]" : "bg-slate-700"}`} />
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.18em] ${event ? "text-cyan-200" : "text-slate-600"}`}>{stage.replaceAll("_", " ")}</p>
            {event?.detail && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{event.detail}</p>}
          </div>
        </div>
      );
    })}
  </div>
);

const PaymentReconciliation = ({ data, syncing, onSync }) => {
  if (!data) return null;
  const summary = [
    ["Orders", data.summary?.orders ?? 0],
    ["Paid", data.summary?.paid_orders ?? 0],
    ["Credits issued", data.summary?.credits_issued ?? 0],
    ["Failures", data.summary?.failed_events ?? 0],
    ["Refunds", data.summary?.refunds ?? 0],
  ];
  return (
    <div className="space-y-4" data-testid="payment-reconciliation">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Razorpay orders, webhook events, failures and refunds</p>
        <Button type="button" onClick={onSync} disabled={syncing} data-testid="payment-sync-btn" className="rounded-full bg-cyan-400 text-slate-950 hover:bg-cyan-300">
          {syncing ? "Syncing…" : "Sync Razorpay"}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {summary.map(([label, value]) => (
          <div key={label} className="rounded-xl bg-black/20 p-3">
            <p className="font-display text-2xl text-white" data-testid={`payment-summary-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Recent payment events</p>
        {(data.webhook_events || []).slice(0, 5).map((event) => (
          <div key={event.event_id} className="rounded-xl border border-teal-500/10 bg-black/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-slate-200">{event.event_type}</p>
              <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${["failed", "rejected"].includes(event.status) ? "bg-red-400/10 text-red-300" : "bg-cyan-400/10 text-cyan-300"}`}>{event.status}</span>
            </div>
            <p className="mt-1 text-[10px] text-slate-500">{event.order_id || "No order"} · {fmtTime(event.created_at)}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Orders & refunds</p>
        {(data.orders || []).slice(0, 5).map((order) => (
          <div key={order.payment_order_id} className="rounded-xl border border-teal-500/10 bg-black/20 p-3 text-xs text-slate-400">
            <div className="flex justify-between gap-3"><span>{order.pack_id} · {order.credits} credits</span><strong className="text-white">₹{order.amount_inr}</strong></div>
            <p className="mt-1">Status: {order.status}{order.payment_status ? ` · Payment: ${order.payment_status}` : ""}{order.refund_status ? ` · Refund: ${order.refund_status}` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const MessageCard = ({ message, expanded, onToggle }) => {
  const isUser = message.role === "user";
  const result = message.result;
  return (
    <motion.article initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={isUser ? "ekam-user-message" : "ekam-assistant-message"}>
      <div className={`${isUser ? "max-w-3xl rounded-3xl rounded-br-md bg-cyan-400 text-slate-950" : "w-full max-w-5xl rounded-3xl rounded-tl-md border border-teal-500/15 bg-[#0a2527]/80 text-slate-200"} px-5 py-4 shadow-xl shadow-black/10`}>
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm sm:text-base font-medium">{message.content}</p>
        ) : (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">
              <span className="rounded-full bg-cyan-400/10 px-2.5 py-1 text-cyan-300">{result?.intent || "Ekam"}</span>
              <span>{result?.verification_status || "processing"}</span>
              {result?.human_review_required && <span className="rounded-full bg-amber-400/10 px-2.5 py-1 text-amber-300">Human review</span>}
            </div>
            <div className="max-w-none text-sm sm:text-base leading-7 text-slate-200" data-testid="ekam-answer-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h1 className="mt-6 mb-4 font-display text-2xl font-medium text-white">{children}</h1>,
                  h2: ({ children }) => <h2 className="mt-7 mb-3 font-display text-xl font-medium text-cyan-200">{children}</h2>,
                  h3: ({ children }) => <h3 className="mt-5 mb-2 text-base font-bold text-white">{children}</h3>,
                  p: ({ children }) => <p className="my-3 leading-7 text-slate-300">{children}</p>,
                  li: ({ children }) => <li className="my-2 leading-7 text-slate-300">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                  a: ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="text-cyan-300 underline decoration-cyan-700 underline-offset-4">{children}</a>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            {result && (
              <div className="mt-5 border-t border-teal-500/10 pt-4">
                <button type="button" onClick={onToggle} data-testid="ekam-show-execution" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-300 hover:text-cyan-200">
                  Show Ekam execution <CaretDown size={14} className={expanded ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
                {expanded && (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <Panel title="Routing" testId="execution-routing">
                      <div className="space-y-2 text-xs text-slate-400">
                        <p><strong className="text-slate-200">Industries:</strong> {(result.industries || []).map((i) => i.name).join(", ")}</p>
                        <p><strong className="text-slate-200">Agents:</strong> {(result.agents || []).slice(0, 12).join(", ")}</p>
                        <p><strong className="text-slate-200">Models:</strong> {(result.models || []).join(", ")}</p>
                        <p><strong className="text-slate-200">Confidence:</strong> {result.confidence}</p>
                      </div>
                    </Panel>
                    <Panel title="Evidence" testId="execution-evidence">
                      <div className="space-y-3">
                        {(result.evidence || []).slice(0, 4).map((item) => (
                          <div key={item.evidence_id} className="rounded-xl bg-black/20 p-3">
                            <p className="text-xs text-slate-300 line-clamp-2">{item.claim}</p>
                            <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-slate-500">{item.provenance} · {item.epistemic_status}</p>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.article>
  );
};

export default function EkamApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState(location.state?.user ? "ready" : "loading");
  const [user, setUser] = useState(location.state?.user || null);
  const [wallet, setWallet] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedModel, setSelectedModel] = useState("auto");
  const [advanced, setAdvanced] = useState(false);
  const [research, setResearch] = useState(false);
  const [running, setRunning] = useState(false);
  const [stages, setStages] = useState([]);
  const [activeNav, setActiveNav] = useState("Chats");
  const [registryData, setRegistryData] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [mobileDrawer, setMobileDrawer] = useState(null);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [search, setSearch] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [billingConfig, setBillingConfig] = useState(null);
  const [proof, setProof] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [selectedPack, setSelectedPack] = useState("starter");
  const [paying, setPaying] = useState(false);
  const { Razorpay, isLoading: razorpayLoading, error: razorpayError } = useRazorpay();
  const fileInput = useRef(null);

  const availableCredits = wallet ? wallet.balance - wallet.locked_balance : 0;
  const currentEstimate = useMemo(() => estimateCredits(prompt, selectedIndustries, advanced), [prompt, selectedIndustries, advanced]);
  const insufficientCredits = !!wallet && prompt.trim().length > 0 && availableCredits < currentEstimate;

  const loadWorkspace = async () => {
    const { data: session } = await api.get("/ekam/auth/session");
    if (!session.authenticated) {
      const { data: inds } = await api.get("/industries");
      setIndustries(inds.industries || []);
      setAuthState("guest");
      return;
    }
    const [{ data: me }, convos, projs, billing, inds] = await Promise.all([
      api.get("/ekam/auth/me"),
      api.get("/ekam/conversations"),
      api.get("/ekam/projects"),
      api.get("/ekam/billing/config"),
      api.get("/industries"),
    ]);
    setUser(me.user);
    setWallet(me.wallet);
    setConversations(convos.data.conversations || []);
    setProjects(projs.data.projects || []);
    setBillingConfig(billing.data);
    setIndustries(inds.data.industries || []);
    setAuthState("ready");
  };

  useEffect(() => {
    if (location.state?.user) {
      loadWorkspace().catch(() => setAuthState("guest"));
      return;
    }
    loadWorkspace().catch(() => setAuthState("guest"));
  }, [location.state?.user]);

  useEffect(() => {
    api.get("/ekam/proof").then(({ data }) => setProof(data)).catch(() => {});
  }, []);

  const requireAuth = () => navigate("/ekam/login", { state: { next: "/" } });

  useEffect(() => {
    const timer = setInterval(() => setExampleIndex((value) => (value + 1) % EXAMPLES.length), 3500);
    return () => clearInterval(timer);
  }, []);

  const selectConversation = async (conversation) => {
    setActiveConversation(conversation.conversation_id);
    setActiveNav("Chats");
    const { data } = await api.get(`/ekam/conversations/${conversation.conversation_id}/messages`);
    const loaded = (data.messages || []).flatMap((run) => [
      { id: `${run.run_id}-user`, role: "user", content: run.user_request || "User request", created_at: run.created_at },
      { id: run.run_id, role: "assistant", content: run.answer, result: run, created_at: run.created_at },
    ]);
    setMessages(loaded);
  };

  const loadRegistry = async (nav) => {
    setActiveNav(nav);
    if (authState === "guest" && !["Agents", "Tools", "Governance"].includes(nav)) {
      setRegistryData({ message: "Sign in to access your projects, workflows, research, artifacts and settings." });
      return;
    }
    if (nav === "Settings" && user?.role === "admin") {
      const [ledger, reconciliation] = await Promise.all([
        api.get("/ekam/admin/ledger"),
        api.get("/ekam/billing/admin/reconciliation"),
      ]);
      setRegistryData(ledger.data);
      setPaymentData(reconciliation.data);
      return;
    }
    if (nav === "Connectors") {
      const [catalog, saved] = await Promise.all([api.get("/ekam/connectors/catalog"), api.get("/ekam/connectors")]);
      setRegistryData({ catalog: catalog.data.connectors, saved: saved.data.connectors });
      return;
    }
    if (nav === "Governance") {
      const [legal, status] = await Promise.all([api.get("/ekam/legal"), api.get("/ekam/status")]);
      setRegistryData({ ...legal.data, status: status.data });
      return;
    }
    const endpoint = {
      Agents: "/agents",
      Workflows: "/workflows",
      Research: "/evidence",
      Tools: "/tools",
      Artifacts: "/runs",
      Plugins: "/ekam/plugins",
      Images: "/ekam/images",
      Coding: "/ekam/code/projects",
      Settings: "/ekam/transactions",
    }[nav];
    if (!endpoint) return;
    const { data } = await api.get(endpoint);
    setRegistryData(data);
  };

  const createProject = async () => {
    if (isGuest) {
      requireAuth();
      return;
    }
    if (!newProjectName.trim()) return;
    const { data } = await api.post("/ekam/projects", { name: newProjectName, type: "Research" });
    setProjects((items) => [data, ...items]);
    setNewProjectName("");
    toast.success("Project created.");
  };

  const setupMfa = async () => {
    const { data } = await api.post("/ekam/auth/mfa/setup");
    setMfaSetup(data);
  };

  const enableMfa = async () => {
    await api.post("/ekam/auth/mfa/enable", { code: mfaCode });
    setMfaSetup(null);
    setMfaCode("");
    setUser((current) => ({ ...current, mfa_enabled: true }));
    toast.success("MFA enabled.");
  };

  const startRazorpay = async () => {
    if (!billingConfig?.enabled || razorpayLoading || !Razorpay) {
      toast.error(razorpayError?.message || "Razorpay is still loading.");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/ekam/billing/orders", { pack_id: selectedPack });
      const pack = billingConfig.packs.find((item) => item.id === selectedPack);
      const checkout = new Razorpay({
        key: data.razorpay.key_id,
        amount: data.razorpay.amount,
        currency: data.razorpay.currency,
        name: "Ekam by Anxya",
        description: `${pack.label} — ${pack.credits} credits`,
        order_id: data.razorpay.id,
        prefill: { name: user.name, email: user.email },
        notes: { user_id: user.user_id, pack_id: selectedPack },
        theme: { color: "#06b6d4" },
        handler: async (response) => {
          const verified = await api.post("/ekam/billing/verify", response);
          setWallet(verified.data.wallet);
          setShowRecharge(false);
          toast.success(`${pack.credits} credits added to your Ekam wallet.`);
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      checkout.on("payment.failed", (response) => toast.error(response.error?.description || "Payment failed."));
      checkout.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Razorpay checkout.");
    } finally {
      setPaying(false);
    }
  };

  const syncPayments = async () => {
    setSyncingPayments(true);
    try {
      await api.post("/ekam/billing/admin/reconciliation/sync");
      const { data } = await api.get("/ekam/billing/admin/reconciliation");
      setPaymentData(data);
      toast.success("Razorpay reconciliation synced.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sync Razorpay.");
    } finally {
      setSyncingPayments(false);
    }
  };

  const uploadAttachment = async () => {
    if (!attachment) return "";
    const form = new FormData();
    form.append("file", attachment);
    const { data } = await api.post("/ekam/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(data.adapter_status === "REAL" ? "Document ingested." : "Document adapter is MOCK / DEMO ONLY for this file type.");
    return `\n\nAttached document: ${data.filename}\nSummary: ${data.summary}\nDocument ID: ${data.document_id}`;
  };

  const submit = async () => {
    if (authState === "guest") {
      requireAuth();
      return;
    }
    if (!prompt.trim() || running) return;
    if (!wallet) {
      toast.info("Wallet is still loading. Try again in a moment.");
      return;
    }
    if (insufficientCredits) {
      setShowRecharge(true);
      return;
    }
    setRunning(true);
    setStages([]);
    const userPrompt = prompt.trim();
    const userMessage = { id: `local-${Date.now()}`, role: "user", content: userPrompt, created_at: new Date().toISOString() };
    setMessages((items) => [...items, userMessage]);
    setPrompt("");
    try {
      const documentContext = await uploadAttachment();
      setAttachment(null);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/solve/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: `${userPrompt}${documentContext}`,
          conversation_id: activeConversation,
          industries: selectedIndustries.length ? selectedIndustries : null,
          task_type: research ? "RESEARCH" : null,
          advanced,
          model: selectedModel === "auto" ? null : selectedModel,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw Object.assign(new Error("Run failed"), { response: error });
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalResult = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const packets = buffer.split("\n\n");
        buffer = packets.pop() || "";
        for (const packet of packets) {
          const line = packet.split("\n").find((item) => item.startsWith("data: "));
          if (!line) continue;
          const event = JSON.parse(line.slice(6));
          if (event.type === "stage") setStages((items) => [...items.filter((item) => item.stage !== event.stage), event]);
          if (event.type === "error") throw Object.assign(new Error(event.message), { event });
          if (event.type === "final") finalResult = event.result;
        }
      }
      if (!finalResult) throw new Error("Run ended without a final result.");
      setActiveConversation(finalResult.conversation_id);
      setMessages((items) => [...items, { id: finalResult.run_id, role: "assistant", content: finalResult.answer, result: finalResult, created_at: finalResult.created_at }]);
      setExpandedRun(finalResult.run_id);
      await loadWorkspace();
      toast.success(finalResult.human_review_required ? "Run completed and queued for human review." : "Run completed.");
    } catch (err) {
      const detail = err.response?.detail || err.event || err.response?.data?.detail;
      if (detail?.code === "INSUFFICIENT_CREDITS") setShowRecharge(true);
      toast.error(detail?.message || detail || err.message || "Run failed.");
    } finally {
      setRunning(false);
    }
  };

  const logout = async () => {
    await api.post("/ekam/auth/logout");
    navigate("/ekam/login", { replace: true });
  };

  if (authState === "loading") {
    return <div className="min-h-screen bg-[#041718] flex items-center justify-center text-slate-400" data-testid="ekam-loading">Loading Ekam…</div>;
  }

  const isGuest = authState === "guest";

  const filteredConversations = conversations.filter((item) => (item.title || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#041718] text-slate-100 overflow-hidden" data-testid="ekam-app">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(6,182,212,0.12),transparent_30%)]" />
      <div className="relative flex h-screen">
        <aside className="hidden w-80 shrink-0 border-r border-teal-500/10 bg-[#041718]/90 backdrop-blur-xl lg:flex lg:flex-col" data-testid="ekam-sidebar">
          <div className="border-b border-teal-500/10 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-2xl font-semibold text-white">Ekam</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-300">Unified Intelligence OS</p>
              </div>
              <Button onClick={() => { setActiveConversation(null); setMessages([]); setStages([]); }} data-testid="ekam-new-chat-btn" className="rounded-full bg-cyan-400 text-slate-950 hover:bg-cyan-300">
                <Plus size={16} className="mr-1" /> New Chat
              </Button>
            </div>
            <div className="mt-5 flex items-center gap-2 rounded-xl border border-teal-500/10 bg-white/[0.03] px-3">
              <ListMagnifyingGlass size={16} className="text-slate-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ekam-search-input" placeholder="Search" className="h-10 w-full bg-transparent text-sm text-slate-200 outline-none placeholder:text-slate-600" />
            </div>
          </div>

          <nav className="grid grid-cols-2 gap-2 p-4" data-testid="ekam-sidebar-nav">
            {NAV_ITEMS.map((item) => (
              <button key={item} type="button" onClick={() => loadRegistry(item)} data-testid={`ekam-nav-${item.toLowerCase()}`} className={`rounded-xl px-3 py-2 text-left text-sm transition-colors ${activeNav === item ? "bg-cyan-400/15 text-cyan-200" : "text-slate-400 hover:bg-white/[0.04] hover:text-white"}`}>
                {item}
              </button>
            ))}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-600">Recent conversations</p>
            <div className="space-y-2">
              {filteredConversations.map((conversation) => (
                <button key={conversation.conversation_id} type="button" onClick={() => selectConversation(conversation)} data-testid={`ekam-conversation-${conversation.conversation_id}`} className={`w-full rounded-xl border px-3 py-3 text-left transition-colors ${activeConversation === conversation.conversation_id ? "border-cyan-400/30 bg-cyan-400/10" : "border-transparent bg-white/[0.03] hover:border-teal-500/15"}`}>
                  <p className="truncate text-sm font-medium text-slate-200">{conversation.title || "Untitled conversation"}</p>
                  <p className="mt-1 text-xs text-slate-500">{fmtTime(conversation.updated_at)}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-teal-500/10 p-4 text-xs text-slate-500">
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Help", testId: "footer-help-link", action: () => window.dispatchEvent(new CustomEvent("ekam:open-assistant")) },
                { label: "Governance", testId: "footer-governance-link", action: () => loadRegistry("Governance") },
                { label: "Privacy", testId: "footer-privacy-link", action: () => navigate("/ekam/privacy") },
                { label: "System Status", testId: "footer-system-status-link", action: () => loadRegistry("Governance") },
              ].map((item) => (
                <button key={item.label} type="button" onClick={item.action} data-testid={item.testId} className="rounded-lg bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.08] hover:text-cyan-300">{item.label}</button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
              {[['About', '/ekam/about'], ['Contact', '/ekam/contact'], ['Trust', '/ekam/trust'], ['Security', '/ekam/security'], ['Terms', '/ekam/terms'], ['Privacy', '/ekam/privacy'], ['Cookies', '/ekam/cookies']].map(([label, url]) => (
                <button key={label} type="button" onClick={() => navigate(url)} data-testid={`footer-legal-${label.toLowerCase()}`} className="text-[10px] text-slate-500 hover:text-cyan-300">{label}</button>
              ))}
            </div>
            <p className="mt-3 text-[10px] text-slate-600">© 2026 Anxya Tech Private Limited</p>
            <button type="button" onClick={isGuest ? requireAuth : undefined} className="mt-4 flex w-full items-center gap-3 rounded-xl border border-teal-500/10 p-3 text-left" data-testid="ekam-user-profile">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-400/10 text-cyan-300"><ShieldCheck size={18} /></span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{isGuest ? "Guest" : user.name}</p>
                <p className="truncate text-xs text-slate-500">{isGuest ? "Sign in to run intelligence" : user.email}</p>
              </div>
            </button>
          </div>
        </aside>

        <main className="relative flex min-w-0 flex-1 flex-col">
          <header className="flex h-16 shrink-0 items-center justify-between border-b border-teal-500/10 bg-[#041718]/80 px-4 sm:px-6 backdrop-blur-xl" data-testid="ekam-topbar">
            <div className="flex items-center gap-2 lg:hidden">
              <button type="button" onClick={() => setMobileDrawer("history")} data-testid="ekam-mobile-history-btn" className="rounded-full border border-teal-500/15 p-2.5 text-slate-300" aria-label="Open workspace history">
                <List size={18} />
              </button>
              <Brain size={24} className="text-cyan-300" />
              <span className="font-display text-xl text-white">Ekam</span>
              <button type="button" onClick={() => setMobileDrawer("context")} data-testid="ekam-mobile-context-btn" className="rounded-full border border-teal-500/15 p-2.5 text-slate-300" aria-label="Open execution panel">
                <Pulse size={18} />
              </button>
            </div>
            <div className="hidden text-xs uppercase tracking-[0.2em] text-slate-500 lg:block">Standard / Advanced Intelligence Workspace</div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setAdvanced(!advanced)} data-testid="ekam-advanced-toggle" className={`hidden sm:inline-flex rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${advanced ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-teal-500/15 text-slate-400"}`}>
                {advanced ? "Advanced" : "Standard"}
              </button>
              <button type="button" onPointerDown={(e) => { e.preventDefault(); if (isGuest) requireAuth(); else setShowRecharge(true); }} data-testid="user-credit-balance" className="inline-flex items-center gap-2 rounded-full border border-teal-500/15 bg-white/[0.03] px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/40">
                <WalletIcon size={16} className="text-cyan-300" /> {isGuest ? "Sign in to run" : `${availableCredits} credits`}
              </button>
              {isGuest ? (
                <button type="button" onClick={requireAuth} data-testid="ekam-login-btn" className="rounded-full bg-cyan-400 px-4 py-2 text-sm font-bold text-slate-950">Sign In</button>
              ) : (
                <button type="button" onClick={logout} data-testid="ekam-logout-btn" className="rounded-full border border-teal-500/15 p-2.5 text-slate-400 hover:text-white"><SignOut size={16} /></button>
              )}
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            <section className="min-w-0 flex-1 overflow-y-auto px-4 sm:px-8 lg:px-12" data-testid="ekam-conversation-view">
              {messages.length === 0 && !running ? (
                <EmptyState example={EXAMPLES[exampleIndex]} onExample={setPrompt} guest={isGuest} proof={proof} />
              ) : (
                <div className="mx-auto max-w-6xl space-y-6 py-8 pb-48">
                  {messages.map((message) => (
                    <MessageCard key={message.id} message={message} expanded={expandedRun === message.id} onToggle={() => setExpandedRun(expandedRun === message.id ? null : message.id)} />
                  ))}
                  {running && (
                    <div className="rounded-3xl border border-cyan-400/20 bg-cyan-400/5 p-6" data-testid="ekam-live-orchestration">
                      <div className="flex items-center gap-3">
                        <span className="h-3 w-3 animate-pulse rounded-full bg-cyan-300" />
                        <p className="text-sm font-semibold text-cyan-200">Ekam is orchestrating…</p>
                      </div>
                      <div className="mt-6"><StageTimeline stages={stages} /></div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {(advanced || activeNav !== "Chats") && (
              <aside className="hidden w-96 shrink-0 overflow-y-auto border-l border-teal-500/10 bg-[#071f21]/70 p-3 pb-40 backdrop-blur-xl xl:block" data-testid="ekam-context-panel">
                <div className="space-y-3">
                  <Panel title="Execution" testId="ekam-execution-panel"><div className="max-h-44 overflow-y-auto"><StageTimeline stages={stages} /></div></Panel>
                  <Panel title="Wallet" testId="ekam-wallet-panel">
                    <p className="font-display text-2xl font-light text-white">{wallet?.balance ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Total credits · {wallet?.locked_balance ?? 0} locked · Razorpay {billingConfig?.mode === "live" ? "live" : "test"} checkout {billingConfig?.enabled ? "enabled" : "pending"}</p>
                  </Panel>
                  {activeNav !== "Chats" && (
                    <Panel title={activeNav} testId="ekam-registry-panel">
                      {activeNav === "Projects" && (
                        <div className="mb-4 flex gap-2">
                          <Input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} data-testid="ekam-project-name-input" placeholder="New project" className="h-10 border-teal-500/15 bg-[#041718] text-white" />
                          <Button onClick={createProject} data-testid="ekam-create-project-btn" className="rounded-xl bg-cyan-400 text-slate-950"><Plus size={16} /></Button>
                        </div>
                      )}
                      {["Connectors", "Plugins", "Images", "Coding", "Governance"].includes(activeNav) && (
                        <EnterprisePanel activeNav={activeNav} data={registryData} onRefresh={() => loadRegistry(activeNav)} />
                      )}
                      {activeNav === "Settings" && user?.role === "admin" && (
                        <div className="mb-4">
                          <PaymentReconciliation data={paymentData} syncing={syncingPayments} onSync={syncPayments} />
                        </div>
                      )}
                      {!["Connectors", "Plugins", "Images", "Coding", "Governance"].includes(activeNav) && (
                        <pre className="max-h-[30rem] overflow-auto whitespace-pre-wrap rounded-xl bg-black/25 p-3 text-[11px] leading-relaxed text-slate-400">{JSON.stringify(activeNav === "Projects" ? { projects } : registryData, null, 2)}</pre>
                      )}
                    </Panel>
                  )}
                  <Panel title="Security" testId="ekam-security-panel">
                    {isGuest ? (
                      <Button onClick={requireAuth} data-testid="ekam-security-signin-btn" className="w-full rounded-full bg-white text-slate-950 hover:bg-cyan-50"><LockKey size={16} className="mr-2" /> Sign in for MFA</Button>
                    ) : user.mfa_enabled ? (
                      <p className="text-sm text-emerald-300">MFA is enabled for this account.</p>
                    ) : (
                      <div className="space-y-3">
                        <Button onClick={setupMfa} data-testid="ekam-mfa-setup-btn" className="w-full rounded-full bg-white text-slate-950 hover:bg-cyan-50"><LockKey size={16} className="mr-2" /> Set up MFA</Button>
                        {mfaSetup && (
                          <div className="rounded-xl border border-cyan-400/20 bg-black/20 p-4">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Authenticator secret</p>
                            <p className="mt-2 break-all font-mono text-sm text-cyan-200" data-testid="ekam-mfa-secret">{mfaSetup.secret}</p>
                            <p className="mt-3 break-all text-[10px] text-slate-500">{mfaSetup.otpauth_url}</p>
                            <div className="mt-4 flex gap-2">
                              <Input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} data-testid="ekam-mfa-enable-input" placeholder="6-digit code" className="h-10 border-teal-500/15 bg-[#041718] text-white" />
                              <Button onClick={enableMfa} data-testid="ekam-mfa-enable-btn" className="rounded-xl bg-cyan-400 text-slate-950">Enable</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Panel>
                </div>
              </aside>
            )}
          </div>

          <div className={`absolute bottom-0 left-0 right-0 ${advanced || activeNav !== "Chats" ? "xl:right-96" : ""} border-t border-teal-500/10 bg-[#041718]/90 p-4 backdrop-blur-xl`} data-testid="ekam-composer-shell">
            <div className="mx-auto max-w-5xl">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <button type="button" onClick={isGuest ? requireAuth : () => fileInput.current?.click()} data-testid="ekam-attach-btn" className="inline-flex items-center gap-1.5 rounded-full border border-teal-500/15 px-3 py-1.5 hover:border-cyan-400/40"><Paperclip size={14} /> Attach</button>
                <input ref={fileInput} type="file" className="hidden" onChange={(e) => setAttachment(e.target.files?.[0] || null)} data-testid="ekam-file-input" />
                <button type="button" onClick={() => setResearch(!research)} data-testid="ekam-research-toggle" className={`rounded-full border px-3 py-1.5 ${research ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-teal-500/15"}`}>Research</button>
                <label className="inline-flex items-center gap-2 rounded-full border border-teal-500/15 px-3 py-1.5">
                  Industry
                  <select value={selectedIndustries[0] || "AUTO"} onChange={(e) => setSelectedIndustries(e.target.value === "AUTO" ? [] : [e.target.value])} data-testid="ekam-industry-select" className="bg-transparent text-cyan-200 outline-none">
                    <option value="AUTO" className="bg-[#041718]">AUTO</option>
                    {industries.map((industry) => <option key={industry.id} value={industry.id} className="bg-[#041718]">{industry.name}</option>)}
                  </select>
                </label>
                <span className="rounded-full border border-teal-500/15 px-3 py-1.5">Tools: AUTO</span>
                <label className="inline-flex items-center gap-2 rounded-full border border-teal-500/15 px-3 py-1.5">
                  Model
                  <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} data-testid="ekam-model-select" className="bg-transparent text-cyan-200 outline-none">
                    <option value="auto" className="bg-[#041718]">AUTO</option>
                    <option value="openai" className="bg-[#041718]">GPT 5.6</option>
                    <option value="anthropic" className="bg-[#041718]">Claude Sonnet</option>
                    <option value="gemini" className="bg-[#041718]">Gemini Flash</option>
                  </select>
                </label>
                <span className={`rounded-full border px-3 py-1.5 ${insufficientCredits ? "border-red-400/40 text-red-300" : "border-teal-500/15"}`}>Est. {currentEstimate} credits</span>
                {attachment && <span className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-cyan-200">{attachment.name}</span>}
              </div>
              <div className="flex items-end gap-3 rounded-[2rem] border border-teal-500/20 bg-[#0a2527]/90 p-3 shadow-2xl shadow-black/30">
                <textarea
                  value={prompt}
                  onFocus={isGuest ? requireAuth : undefined}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  data-testid="ekam-prompt-input"
                  placeholder="Ask anything or describe the outcome you want..."
                  rows={3}
                  className="max-h-44 min-h-16 flex-1 resize-none bg-transparent px-3 py-3 text-base text-white outline-none placeholder:text-slate-600"
                />
                <button type="button" data-testid="ekam-voice-btn" className="rounded-full border border-teal-500/15 p-3 text-slate-500" title="Voice-ready interface"><Microphone size={18} /></button>
                <button type="button" onClick={submit} disabled={!isGuest && (running || !wallet || !prompt.trim())} data-testid="ai-run-command-submit" className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 text-slate-950 transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-40">
                  <ArrowUpRight size={22} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <AnimatePresence>
        {mobileDrawer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" data-testid="ekam-mobile-drawer" onClick={() => setMobileDrawer(null)}>
            <motion.aside
              initial={{ x: mobileDrawer === "history" ? -320 : 320 }}
              animate={{ x: 0 }}
              exit={{ x: mobileDrawer === "history" ? -320 : 320 }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className={`absolute top-0 h-full w-[86vw] max-w-sm overflow-y-auto border-teal-500/10 bg-[#041718] p-5 shadow-2xl ${mobileDrawer === "history" ? "left-0 border-r" : "right-0 border-l"}`}
              data-testid={`ekam-mobile-${mobileDrawer}-drawer`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display text-2xl font-semibold text-white">Ekam</p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-300">{mobileDrawer === "history" ? "Workspace" : "Execution & Settings"}</p>
                </div>
                <button type="button" onClick={() => setMobileDrawer(null)} data-testid="ekam-mobile-drawer-close" className="rounded-full border border-teal-500/15 p-2 text-slate-400"><X size={16} /></button>
              </div>

              {mobileDrawer === "history" ? (
                <div className="mt-6 space-y-5">
                  <Button onClick={() => { setActiveConversation(null); setMessages([]); setStages([]); setMobileDrawer(null); }} data-testid="ekam-mobile-new-chat-btn" className="w-full rounded-full bg-cyan-400 text-slate-950 hover:bg-cyan-300"><Plus size={16} className="mr-2" /> New Chat</Button>
                  <div className="flex items-center gap-2 rounded-xl border border-teal-500/10 bg-white/[0.03] px-3">
                    <ListMagnifyingGlass size={16} className="text-slate-500" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ekam-mobile-search-input" placeholder="Search" className="h-10 w-full bg-transparent text-sm text-slate-200 outline-none placeholder:text-slate-600" />
                  </div>
                  <nav className="grid grid-cols-2 gap-2">
                    {NAV_ITEMS.map((item) => (
                      <button key={item} type="button" onClick={() => { loadRegistry(item); setMobileDrawer(item === "Chats" ? "history" : "context"); }} data-testid={`ekam-mobile-nav-${item.toLowerCase()}`} className={`rounded-xl px-3 py-2 text-left text-sm ${activeNav === item ? "bg-cyan-400/15 text-cyan-200" : "bg-white/[0.03] text-slate-400"}`}>{item}</button>
                    ))}
                  </nav>
                  <div>
                    <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-600">Recent conversations</p>
                    <div className="space-y-2">
                      {filteredConversations.map((conversation) => (
                        <button key={conversation.conversation_id} type="button" onClick={() => { selectConversation(conversation); setMobileDrawer(null); }} data-testid={`ekam-mobile-conversation-${conversation.conversation_id}`} className="w-full rounded-xl bg-white/[0.03] px-3 py-3 text-left">
                          <p className="truncate text-sm font-medium text-slate-200">{conversation.title || "Untitled conversation"}</p>
                          <p className="mt-1 text-xs text-slate-500">{fmtTime(conversation.updated_at)}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-5 pb-10">
                  <Panel title="Execution" testId="ekam-mobile-execution-panel"><StageTimeline stages={stages} /></Panel>
                  <Panel title="Wallet" testId="ekam-mobile-wallet-panel">
                    <p className="font-display text-4xl font-light text-white">{wallet?.balance ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">{isGuest ? "Sign in to activate credits" : `${wallet?.locked_balance ?? 0} locked`}</p>
                  </Panel>
                  {activeNav !== "Chats" && (
                    <Panel title={activeNav} testId="ekam-mobile-registry-panel">
                      {["Connectors", "Plugins", "Images", "Coding", "Governance"].includes(activeNav) ? (
                        <EnterprisePanel activeNav={activeNav} data={registryData} onRefresh={() => loadRegistry(activeNav)} />
                      ) : (
                        <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl bg-black/25 p-3 text-[11px] text-slate-400">{JSON.stringify(activeNav === "Projects" ? { projects } : registryData, null, 2)}</pre>
                      )}
                    </Panel>
                  )}
                  <Panel title="Security" testId="ekam-mobile-security-panel">
                    <Button onClick={isGuest ? requireAuth : () => { setMobileDrawer(null); setActiveNav("Settings"); }} data-testid="ekam-mobile-security-btn" className="w-full rounded-full bg-white text-slate-950 hover:bg-cyan-50"><LockKey size={16} className="mr-2" /> {isGuest ? "Sign in for MFA" : "Open Security"}</Button>
                  </Panel>
                </div>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRecharge && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="ekam-recharge-modal">
            <motion.div initial={{ y: 20, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.98 }} className="w-full max-w-lg rounded-3xl border border-cyan-400/25 bg-[#0a2527] p-8 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Credits required</p>
                  <h2 className="mt-3 font-display text-3xl font-light text-white">Recharge to run intelligence.</h2>
                </div>
                <button onClick={() => setShowRecharge(false)} data-testid="ekam-recharge-close" className="rounded-full border border-teal-500/15 p-2 text-slate-400"><X size={16} /></button>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {(billingConfig?.packs || []).map((pack) => (
                  <button
                    key={pack.id}
                    type="button"
                    onClick={() => setSelectedPack(pack.id)}
                    data-testid={`razorpay-pack-${pack.id}`}
                    className={`rounded-2xl border p-4 text-left transition-colors ${selectedPack === pack.id ? "border-cyan-400 bg-cyan-400/10" : "border-teal-500/15 bg-white/[0.03] hover:border-cyan-400/40"}`}
                  >
                    <p className="font-display text-2xl text-white">{pack.credits}</p>
                    <p className="text-xs text-slate-500">credits · ₹{pack.amount_inr}</p>
                    <p className="mt-1 text-[10px] text-slate-600">₹{pack.base_inr} list · save ₹{pack.savings_inr}</p>
                    <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-300">{pack.label} · {pack.discount_percent}% off</p>
                  </button>
                ))}
              </div>
              <p className="mt-5 rounded-xl border border-cyan-400/25 bg-cyan-400/10 p-4 text-sm leading-relaxed text-cyan-100">
                Razorpay {billingConfig?.mode === "live" ? "live" : "test"} checkout is connected. Credits are ₹51 each before package discounts.
              </p>
              <Button onClick={startRazorpay} disabled={!billingConfig?.enabled || paying} data-testid="ekam-razorpay-pay-btn" className="mt-6 w-full rounded-full bg-cyan-400 text-slate-950 disabled:opacity-40">
                {paying ? "Opening Razorpay…" : `Pay with Razorpay — ₹${billingConfig?.packs?.find((pack) => pack.id === selectedPack)?.amount_inr || 0}`}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AskEkamWidget />
    </div>
  );
}
